<template>
    <eSesjaModal>
        <eSesjaModalHeader @close="closeModal">
            {{ $t("com.global.edit") }} {{ $t("com.discussion.profile.modes") }}
        </eSesjaModalHeader>
        <eSesjaModalBody>
            <section class="discussion-profile-mode-form" v-if="editingMode && availableTypesForEdit.length">
                <section class="discussion-profile-mode-form__row">
                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-type") }}</label>
                    <select v-model.number="editingMode.typeId" class="edit-point-form__input" @change="syncEditedTypeName">
                        <option v-for="type in availableTypesForEdit" :key="type.id" :value="type.id">
                            {{ type.name }}
                        </option>
                    </select>
                </section>

                <section class="discussion-profile-mode-form__row">
                    <section class="discussion-profile-mode-form__switch-row">
                        <InputSwitch id="edit-mode-block-modal" v-model="editingMode.isBlock" />
                        <label class="edit-point-form__label discussion-profile-mode-form__switch-label" for="edit-mode-block-modal">
                            {{ $t("com.discussion.profile.mode-block") }}
                        </label>
                    </section>
                </section>

                <section class="discussion-profile-mode-form__row">
                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-first-statement") }}</label>
                    <select v-model.number="editingMode.firstStatement" class="edit-point-form__input" :disabled="editingMode.isBlock">
                        <option v-for="option in statementTimeOptions" :key="`edit-first-${option.id}`" :value="option.id">
                            {{ $t(option.textKey) }}
                        </option>
                    </select>
                </section>

                <section class="discussion-profile-mode-form__row">
                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-second-statement") }}</label>
                    <select v-model.number="editingMode.secondStatement" class="edit-point-form__input" :disabled="editingMode.isBlock">
                        <option v-for="option in statementTimeOptions" :key="`edit-second-${option.id}`" :value="option.id">
                            {{ $t(option.textKey) }}
                        </option>
                    </select>
                </section>

                <section class="discussion-profile-mode-form__row">
                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-third-statement") }}</label>
                    <select v-model.number="editingMode.thirdStatement" class="edit-point-form__input" :disabled="editingMode.isBlock">
                        <option v-for="option in statementTimeOptions" :key="`edit-third-${option.id}`" :value="option.id">
                            {{ $t(option.textKey) }}
                        </option>
                    </select>
                </section>

                <section class="discussion-profile-mode-form__row">
                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-next-statement") }}</label>
                    <select v-model.number="editingMode.nextStatement" class="edit-point-form__input" :disabled="editingMode.isBlock">
                        <option v-for="option in statementTimeOptions" :key="`edit-next-${option.id}`" :value="option.id">
                            {{ $t(option.textKey) }}
                        </option>
                    </select>
                </section>

                <section class="discussion-profile-mode-form__row">
                    <section class="discussion-profile-mode-form__switch-row">
                        <InputSwitch id="edit-mode-turn-off-modal" v-model="editingMode.turnOff" :disabled="editingMode.isBlock" />
                        <label class="edit-point-form__label discussion-profile-mode-form__switch-label" for="edit-mode-turn-off-modal">
                            {{ $t("com.discussion.profile.mode-turn-off") }}
                        </label>
                    </section>
                </section>

                <section class="discussion-profile-mode-form__row">
                    <section class="discussion-profile-mode-form__switch-row">
                        <InputSwitch id="edit-mode-sound-modal" v-model="editingMode.isSoundNotification" :disabled="editingMode.isBlock" />
                        <label class="edit-point-form__label discussion-profile-mode-form__switch-label" for="edit-mode-sound-modal">
                            {{ $t("com.discussion.profile.mode-sound-notification") }}
                        </label>
                    </section>
                </section>
            </section>
            <section v-else class="text-center">
                {{ $t("com.global.no-data") }}
            </section>
        </eSesjaModalBody>
        <eSesjaModalFooter>
            <eSesjaModalButtonCancel @close="closeModal">
                {{ $t("com.global.cancel") }}
            </eSesjaModalButtonCancel>
            <eSesjaModalButtonSave :disabled="isSaveDisabled" @save="saveMode">
                {{ $t("com.global.save") }}
            </eSesjaModalButtonSave>
        </eSesjaModalFooter>
    </eSesjaModal>
</template>

<script charset="utf-8">
import AlertHelper from "@/helpers/alerts.helper.js";
import DiscussionProfileService from "@/services/discussion.profile.service";
import InputSwitch from "@/components/BasicControls/InputSwitch.vue";

export default {
    name: "EditDiscussionProfileModeModal",
    components: {
        InputSwitch
    },
    props: {
        discussionId: {
            type: Number,
            required: true
        },
        mode: {
            type: Object,
            required: true
        },
        statementTypes: {
            type: Array,
            default: () => []
        },
        statementTimeOptions: {
            type: Array,
            default: () => []
        },
        usedTypeIds: {
            type: Array,
            default: () => []
        }
    },
    emits: ["close", "updated"],
    data() {
        return {
            isSaving: false,
            editingMode: null
        };
    },
    computed: {
        availableTypesForEdit() {
            return this.statementTypes.filter((type) => {
                if (type.id === this.editingMode?.typeId) {
                    return true;
                }

                return !this.usedTypeIds.includes(type.id);
            });
        },
        isSaveDisabled() {
            return this.isSaving || !this.editingMode || this.editingMode.typeId <= 0;
        }
    },
    mounted() {
        this.resetForm();
    },
    methods: {
        resetForm() {
            this.editingMode = {
                id: this.mode.id,
                type: this.mode.type,
                typeId: Number(this.mode.typeId),
                isBlock: !!this.mode.isBlock,
                firstStatement: this.mode.firstStatement,
                secondStatement: this.mode.secondStatement,
                thirdStatement: this.mode.thirdStatement,
                nextStatement: this.mode.nextStatement,
                turnOff: !!this.mode.turnOff,
                isSoundNotification: !!this.mode.isSoundNotification
            };
        },
        syncEditedTypeName() {
            if (!this.editingMode) {
                return;
            }

            const selectedType = this.statementTypes.find((type) => type.id === this.editingMode.typeId);
            this.editingMode.type = selectedType?.name || "";
        },
        normalizeNumber(value) {
            const parsed = Number(value);

            if (!Number.isFinite(parsed)) {
                return 0;
            }

            return parsed;
        },
        closeModal() {
            this.$emit("close");
        },
        async saveMode() {
            if (this.isSaveDisabled) {
                return;
            }

            try {
                this.isSaving = true;

                await DiscussionProfileService.UpdateMode(this.discussionId, this.editingMode.id, {
                    id: this.editingMode.id,
                    type: this.editingMode.type,
                    typeId: this.editingMode.typeId,
                    isBlock: this.editingMode.isBlock,
                    firstStatement: this.normalizeNumber(this.editingMode.firstStatement),
                    secondStatement: this.normalizeNumber(this.editingMode.secondStatement),
                    thirdStatement: this.normalizeNumber(this.editingMode.thirdStatement),
                    nextStatement: this.normalizeNumber(this.editingMode.nextStatement),
                    turnOff: this.editingMode.turnOff,
                    isSoundNotification: this.editingMode.isSoundNotification
                });

                this.$emit("updated");
                this.closeModal();
            } catch (e) {
                if (e.response !== undefined) {
                    const t = this.$swal.mixin(AlertHelper.errorToastElement());
                    t.fire(
                        AlertHelper.errorToastAlert(
                            this.$t("com.global.default-save-error"),
                            this.$t("com.global.error")
                        )
                    );
                }
            } finally {
                this.isSaving = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.discussion-profile-mode-form {
    display: flex;
    flex-direction: column;
    row-gap: 16px;

    &__row {
        display: flex;
        flex-direction: column;
    }

    &__switch-row {
        display: flex;
        align-items: center;
        column-gap: 12px;
        min-height: 38px;
    }

    &__switch-label {
        margin: 0;
    }
}

.edit-point-form__input {
    width: 100%;
    padding: 12.5px 16px;
    background: #ffffff;
    border: 1px solid #d1d6df;
    box-sizing: border-box;
    box-shadow: 1px 1px 3px rgba(0, 8, 44, 0.06);
    border-radius: 4px;
    margin: 8px 0px;
    font-size: 16px;
    line-height: 144%;
    color: #00082c;

    &:focus {
        border: 1px solid #00082c;
    }
}

.edit-point-form__label {
    font-weight: 600;
    font-size: 14px;
    line-height: 145%;
    color: #00082c;
}
</style>
